/* Terms of Service — the master contract every verqo user accepts at
   signup. Drafted to maximise platform protection: limitation of
   liability, no-reverse-engineer / no-scrape, indemnification, dispute
   resolution by binding arbitration in Delaware, IP ownership, and a
   broad termination clause for any abuse.

   IMPORTANT: this is engineering's plain-English baseline. Run the
   final wording past actual fintech counsel before going live with
   paying customers. A footer disclaimer notes this. */
import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-terms">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <ScrollText className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Terms of Service</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            These Terms of Service ("Terms") form a binding agreement between
            you (or the entity you represent) and verqo, Inc. ("verqo", "we",
            "us"). By creating an account or using the Platform, you accept
            these Terms in full. If you don't accept them, don't use the Platform.
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. Eligibility &amp; account</h2>
          <ul>
            <li>You must be a legal business entity (LLC, corp, sole-prop, etc.) with valid US tax ID, or an authorised representative of one.</li>
            <li>You must be 18+ and authorised to bind the entity to these Terms.</li>
            <li>One business = one account. Sharing logins is prohibited.</li>
            <li>You must keep your account information accurate. We may suspend or close accounts with stale or false information.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">2. The Platform &amp; financial services</h2>
          <p>
            verqo is a B2B operating system. We provide invoicing, credit underwriting,
            trade credit insurance, payments, shipping, and related services through
            our own technology and via integrations with third-party providers (Plaid,
            Middesk, Allianz Trade, Modern Treasury, Verqo Shipping, Resend,
            and others). verqo is not a bank. Banking and money-movement services are
            provided by our regulated banking partners. Trade credit insurance is
            issued by Allianz Trade.
          </p>
          <p>
            verqo computes a behaviour-based score and surfaces peer-anchored
            tier reference points (suggested terms, reference limits) using KYB,
            banking, and trade-credit signals. <strong>These outputs are
            informational only and do not constitute a credit decision,
            approval, or recommendation from verqo.</strong> Each lender,
            seller, or banking partner sets its own underwriting policy and
            remains solely responsible for the credit decisions it makes —
            including the right to approve, deny, override, or modify any
            verqo-computed reference point in either direction. verqo is not a
            credit reporting agency under 15 U.S.C. § 1681a(f) and does not
            assemble or evaluate consumer credit information.
          </p>

          <h2 className="display text-xl mt-8 mb-2">3. Acceptable use</h2>
          <p>
            Your use of the Platform is governed by our <Link to="/legal/acceptable-use" className="underline">Acceptable Use Policy</Link>,
            which is incorporated by reference. Violations are grounds for
            immediate suspension or termination.
          </p>

          <h2 className="display text-xl mt-8 mb-2" id="invoice-validity">4. Invoice validity &amp; binding acceptance</h2>
          <p>
            On the Platform, the binding contract between a seller and buyer is
            formed when the buyer <strong>accepts</strong> an issued invoice — either
            by paying it (Prepay) or by authorizing it under a credit term (Net-X).
            An issued-but-unaccepted invoice is an <em>offer</em>, not a contract.
          </p>
          <p>
            Each seller configures an <strong>invoice validity window</strong> (default
            30 days, range 1–365). This window is published on the seller's
            public profile and printed on every invoice PDF generated by the
            Platform. If the buyer does not accept the invoice within the
            window, the invoice <strong>auto-voids</strong> and the Platform may
            reject any subsequent payment or authorization attempt for that
            invoice. The seller may then re-issue at the then-current price; the
            buyer may also send a new purchase order. Already-accepted invoices
            (status: <code>paid</code>, <code>authorized</code>, <code>financed</code>,
            <code>credit_terms</code>, and similar) are <strong>never</strong>
            auto-voided — once accepted, price is locked for the duration of the
            agreed Net-X term.
          </p>
          <p className="text-sm text-foreground/70">
            <em>Practical implications:</em> sellers should choose a validity
            window that reflects their cost-of-goods volatility; buyers should
            either pay/authorize promptly within the published window or
            request a fresh quote from the seller after the window closes.
          </p>

          <h2 className="display text-xl mt-8 mb-2">5. Fees</h2>
          <p>
            Some Platform features carry transaction or subscription fees,
            disclosed in the application or in your tenant settings. Fees are
            non-refundable except where required by law. We may change fees on
            at least 30 days' notice; continued use after the change is acceptance.
          </p>

          <h2 className="display text-xl mt-8 mb-2">6. Your content &amp; data</h2>
          <ul>
            <li>You retain ownership of all data you upload (orders, invoices, products, customer lists).</li>
            <li>You grant verqo a non-exclusive, worldwide, royalty-free licence to host, process, transmit, and display your data only as needed to operate the Platform.</li>
            <li>You warrant that you have the right to upload and share that data, and that doing so doesn't violate any third-party rights.</li>
            <li>Personal data of your team members and customers is processed under our <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">7. Our intellectual property</h2>
          <p>
            verqo, the verqo logo, the Platform's source code, design, UX
            patterns, dashboards, prompts, scoring weights, AI models, and all
            related trade secrets are the exclusive property of verqo. Nothing
            in these Terms transfers any ownership to you. You receive a limited,
            revocable, non-transferable, non-sublicensable licence to use the
            Platform during the term of your subscription. You may not:
          </p>
          <ul>
            <li>Reverse engineer, decompile, or attempt to derive the source code of any part of the Platform.</li>
            <li>Scrape, crawl, frame, embed, or systematically copy any portion of the Platform's UI, workflows, or data.</li>
            <li>Use the Platform to train or improve a competing product or AI model.</li>
            <li>Resell, sublicense, or "white-label" the Platform without a written agreement from verqo.</li>
            <li>Remove or alter any verqo trademark, copyright, or proprietary notice.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">8. Confidentiality</h2>
          <p>
            Both parties may exchange confidential information. Each party
            agrees to (a) use it only as needed under these Terms, (b) protect
            it with the same care as their own confidential information (and
            not less than reasonable care), and (c) not disclose it to anyone
            except employees, contractors, or advisors with a need to know who
            are bound by similar confidentiality.
          </p>

          <h2 className="display text-xl mt-8 mb-2">9. Disclaimers</h2>
          <p className="uppercase text-[12px]">
            The Platform is provided "as is" and "as available". verqo disclaims
            all warranties, express or implied, including merchantability,
            fitness for a particular purpose, and non-infringement, except as
            expressly stated. We do not warrant that the Platform will be
            uninterrupted, error-free, or perfectly secure.
          </p>

          <h2 className="display text-xl mt-8 mb-2">10. Limitation of liability</h2>
          <p className="uppercase text-[12px]">
            To the maximum extent permitted by law: (a) neither party will be
            liable for indirect, incidental, special, consequential, or
            punitive damages (including lost profits, lost revenue, lost data);
            and (b) each party's total cumulative liability arising out of or
            related to these Terms is capped at the greater of (i) the fees you
            paid verqo in the 12 months preceding the claim, or (ii) US$1,000.
          </p>

          <h2 className="display text-xl mt-8 mb-2">11. Indemnification</h2>
          <p>
            You will defend, indemnify, and hold verqo harmless from any
            third-party claim arising from (a) your use of the Platform in
            violation of these Terms, (b) your data, or (c) your products
            sold or shipped through the Platform. verqo will defend, indemnify,
            and hold you harmless from any third-party claim that the Platform
            (excluding your data and any third-party services) infringes a US
            patent, copyright, or trademark.
          </p>

          <h2 className="display text-xl mt-8 mb-2">12. Term &amp; termination</h2>
          <p>
            These Terms remain in effect until your account is closed. Either
            party may terminate for material breach with 30 days' notice and an
            opportunity to cure. We may terminate or suspend immediately for
            (a) suspected fraud or AML violations, (b) breach of the Acceptable
            Use Policy, or (c) regulatory order. On termination, you remain
            liable for any outstanding invoices and we retain transactional
            records as required by federal AML / tax law (typically 7 years).
          </p>

          <h2 className="display text-xl mt-8 mb-2">13. Governing law &amp; arbitration</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware,
            without regard to conflict-of-laws rules. Any dispute must be
            resolved by binding individual arbitration administered by the
            American Arbitration Association in Wilmington, Delaware. You and
            verqo each waive the right to a jury trial and the right to
            participate in a class action. Either party may seek injunctive
            relief in any court of competent jurisdiction to protect IP or
            confidential information.
          </p>

          <h2 className="display text-xl mt-8 mb-2">14. Changes</h2>
          <p>
            We may revise these Terms or any referenced Policy at any time by
            posting the revised version at <a href="https://app.verqo.com/legal" className="underline">app.verqo.com/legal</a>. The revised version becomes effective
            when posted. <strong>Your continued use of the Service after the effective date
            constitutes your acceptance of the revised Terms.</strong> We may, but are not
            required to, notify you of material changes by email; failure to provide
            such notice does not waive the modifications. By creating an account you
            authorize verqo to update these Terms going forward without obtaining a
            fresh signed acceptance for each revision.
          </p>

          <h2 className="display text-xl mt-8 mb-2">15. Miscellaneous</h2>
          <ul>
            <li>If any provision is held unenforceable, the rest stays in effect.</li>
            <li>You may not assign these Terms without our written consent. We may assign in connection with a merger, acquisition, or asset sale.</li>
            <li>These Terms, together with the linked policies, are the entire agreement and supersede prior agreements on the same subject.</li>
            <li>Notices to verqo: <a href="mailto:legal@verqo.com" className="underline">legal@verqo.com</a>. Notices to you: the email on your account.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
